import React, { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import {
  Button,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  selectedLmsItemId,
  setLmsFormMode,
} from '../../lmsSlice'
import {
  labDeviceTableColumns as columns,
  labDeviceEnabledColumn,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
  listenColumns,
  connnectColumns,
  labDeviceModelListenMode,
  labdeviceModelColumn,
  labDeviceOperatingUnitColumn,
  labDeviceModelConnectMode,
  getModel,
  SUBMIT,
} from '../../constants'
import { toast } from 'react-toastify'
import {
  createDevice,
  getAllLabDeviceModels,
  getDeviceById,
  updateDeviceById,
} from '../../../../services/LmsService'
import '../../lms.scss'
import { selectLocations } from '../../../location/locationSlice'
import * as Yup from 'yup'
import { emptyString } from '../../../Radiology/Constants/constants'

export type ColumnType = {
  name: string
  label: string
  mandatory: boolean
  dropDown?: any[]
}

export type deviceModel = {
  name: string
  mode: string
  id: number
}

const CreateUpdateLabDevice = () => {
  const mode = useAppSelector(selectedLmsFormMode)
  const selectedLMSItemId = useAppSelector(selectedLmsItemId)
  const dispatch = useAppDispatch()
  const locations = useAppSelector(selectLocations)
  const [models, setModels] = useState<deviceModel[]>([])

  async function validateValues(values: Record<string, any>) {
    if (getModel(models, values)?.mode === labDeviceModelListenMode) {
      values.external_port = emptyString
      values.device_ip = emptyString
    } else {
      values.internal_port = emptyString
    }

    if (mode === lmsFormCreateMode) {
      addDevice(values)
    } else {
      updateDevice(values)
    }
  }

  async function addDevice(values: Record<string, any>) {
    values.model_id = getModel(models, values)?.id
    const response = await createDevice(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  async function updateDevice(values: any) {
    values.model_id = getModel(models, values)?.id
    const response = await updateDeviceById(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  const {
    values,
    setFieldValue,
    setValues,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
      facility: '',
      location: '',
      device_ip: '',
      internal_port: '',
      external_port: '',
      enabled: true,
      model_id: '',
    },
    validationSchema: Yup?.object({
      name: Yup?.string()?.trim()?.required(),
      model_id: Yup?.string()?.required(),
      facility: Yup?.string()?.required(),
    }),
    onSubmit: (values) => {
      validateValues(values)
    },
  })

  const setDropDown = useCallback(async () => {
    columns?.forEach(async (column: ColumnType) => {
      let models: any
      let modelsObj: any
      let location: any
      switch (column?.name) {
        case labdeviceModelColumn:
          models = await getAllLabDeviceModels(0, -1)
          if (models?.success) {
            modelsObj = models?.data?.data?.map((item: any) => {
              return {
                name: item?.name,
                mode: item?.default_connection_type,
                id: item?.id,
              }
            })
            setModels(modelsObj)
            column.dropDown = Array?.isArray(
              modelsObj?.map((model: any) => model?.name)
            )
              ? modelsObj?.map((model: any) => model?.name)
              : []
          } else {
            toast?.error(models?.message)
          }
          break
        case labDeviceOperatingUnitColumn:
          location = locations?.map((item: any) => item?.resource?.name)
          column.dropDown = Array?.isArray(location) ? location : []
          break
      }
    })
  }, [locations])

  const getDevice = useCallback(async () => {
    const response = await getDeviceById(selectedLMSItemId)
    if (response?.success) {
      setValues(response?.data)
      setFieldValue(labdeviceModelColumn, response?.data?.device_model?.name)
    } else {
      toast?.error(response?.message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLMSItemId, mode])

  useEffect(() => {
    if (mode === lmsFormEditMode) {
      getDevice()
    }
  }, [mode, getDevice])

  useEffect(() => {
    setDropDown()
  }, [mode, setDropDown])

  return (
    <form onSubmit={handleSubmit} className='labDevicesFormStyle'>
      <div className='labDeviceFormFirstDivStyle'>
        {columns?.map((column, index) => (
          <div className='labDeviceFormSecondDivStyle' key={index}>
            <div className='labDeviceFormThirdDivStyle'>
              {!(
                mode === lmsFormCreateMode &&
                column?.name === labDeviceEnabledColumn
              ) && (
                <label
                  id={column?.name}
                  aria-labelledby={column?.name}
                  className='labDeviceFormLabelStyle fieldSet'
                >
                  {column?.label}&nbsp;
                  {column?.mandatory && <span className='required_red'>*</span>}
                  &nbsp; :
                </label>
              )}
            </div>
            <div className='labDeviceFormTextFieldDiv'>
              {column?.name !== labDeviceEnabledColumn && !column?.dropDown ? (
                <div className='formValidationErrorStyle'>
                  <TextField
                    className='labDeviceFormTextFieldStyle'
                    id={column?.name}
                    disabled={
                      (listenColumns?.includes(column?.name) &&
                        getModel(models, values)?.mode ===
                          labDeviceModelConnectMode) ||
                      (connnectColumns?.includes(column?.name) &&
                        getModel(models, values)?.mode ===
                          labDeviceModelListenMode)
                    }
                    value={values?.[column?.name as keyof typeof values]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      !!touched?.[column?.name as keyof typeof touched] &&
                      !!errors?.[column?.name as keyof typeof errors]
                    }
                    size='small'
                  />
                  {touched?.[column?.name as keyof typeof touched] &&
                    errors?.[column?.name as keyof typeof touched] && (
                      <Typography variant='caption' color='error'>
                        {errors?.[column?.name as keyof typeof touched]}
                      </Typography>
                    )}
                </div>
              ) : (
                <div className='formValidationErrorStyle'>
                  <Select
                    className='labDeviceFormTextFieldStyle'
                    id={column?.name}
                    name={column?.name}
                    aria-labelledby={column?.name}
                    value={values?.[column?.name as keyof typeof values] || ''}
                    onChange={(event) => {
                      setFieldValue(column?.name, event?.target?.value)
                    }}
                    input={<OutlinedInput className='selectFieldBorder' />}
                    onBlur={handleBlur}
                    error={
                      !!touched?.[column?.name as keyof typeof touched] &&
                      !!errors?.[column?.name as keyof typeof errors]
                    }
                  >
                    {Array?.isArray(column?.dropDown) &&
                      column?.dropDown?.map((option: string) => (
                        <MenuItem key={crypto?.randomUUID()} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                  </Select>
                  {touched?.[column?.name as keyof typeof touched] &&
                    errors?.[column?.name as keyof typeof touched] && (
                      <Typography variant='caption' color='error'>
                        {errors?.[column?.name as keyof typeof touched]}
                      </Typography>
                    )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className='labDeviceFormSubmitDivButtonStyle'>
        <Button
          variant='contained'
          type='submit'
          size='small'
          className='labDeviceFormSubmitButtonStyle'
        >
          {SUBMIT}
        </Button>
      </div>
    </form>
  )
}

export default CreateUpdateLabDevice
